/**
 * Lista de Desejos — App Proxy + login obrigatório (spec 0097 / ADR-070).
 * API global: window.GmWishlist
 *
 * Logado: /apps/wishlist (customer.metafields.custom.wishlist)
 * Guest: redirect para login (sem localStorage)
 */
(function () {
  try {
    if (new URLSearchParams(window.location.search).get('wl') === 'modal') {
      document.documentElement.classList.add('gm-wl-modal');
    }
  } catch (e) {
    /* URL indisponível */
  }

  const LEGACY_STORAGE_KEY = 'gm-wishlist';
  const LEGACY_MERGED_KEY = 'gm-wishlist-merged';
  const CHANGE_EVENT = 'gm:wishlist:change';
  const PROXY_DEFAULT = '/apps/wishlist';
  const LOGIN_DEFAULT = '/account/login';

  /** @type {{ loggedIn: boolean, proxyBase: string, customerId: string|null, loginUrl: string, serverItems: object[] }} */
  let boot = {
    loggedIn: false,
    proxyBase: PROXY_DEFAULT,
    customerId: null,
    loginUrl: LOGIN_DEFAULT,
    serverItems: [],
  };

  /** Cache em memória quando logado. */
  let memoryItems = null;

  /** Serializa requests remotas. */
  let remoteQueue = Promise.resolve();

  function clearLegacyStorage() {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      localStorage.removeItem(LEGACY_MERGED_KEY);
    } catch (e) {
      /* private mode */
    }
  }

  function readBoot() {
    const el = document.getElementById('gm-wishlist-boot');
    if (!el) {
      const flags = document.getElementById('gm-wishlist-flags');
      if (flags) {
        boot.loggedIn = flags.getAttribute('data-gm-customer-logged-in') === 'true';
        boot.proxyBase =
          flags.getAttribute('data-gm-wishlist-proxy') || PROXY_DEFAULT;
        boot.loginUrl =
          flags.getAttribute('data-gm-wishlist-login') || LOGIN_DEFAULT;
      }
      return boot;
    }
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      boot = {
        loggedIn: Boolean(parsed.loggedIn),
        proxyBase: parsed.proxyBase || PROXY_DEFAULT,
        customerId: parsed.customerId != null ? String(parsed.customerId) : null,
        loginUrl: parsed.loginUrl || LOGIN_DEFAULT,
        serverItems: Array.isArray(parsed.serverItems)
          ? parsed.serverItems.map(normalize)
          : [],
      };
    } catch (e) {
      /* ignore */
    }
    return boot;
  }

  function isLoggedIn() {
    return Boolean(boot.loggedIn);
  }

  function useRemote() {
    return isLoggedIn();
  }

  function loginRedirectUrl() {
    const base = boot.loginUrl || LOGIN_DEFAULT;
    if (base.indexOf('return_url=') !== -1) return base;
    const sep = base.indexOf('?') === -1 ? '?' : '&';
    const ret = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    return `${base}${sep}return_url=${ret}`;
  }

  function requireLogin() {
    window.location.href = loginRedirectUrl();
  }

  function emit(items) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
  }

  function itemKey(item) {
    if (item.id != null && item.id !== '') return `id:${item.id}`;
    if (item.handle) return `handle:${item.handle}`;
    if (item.url) return `url:${item.url}`;
    return `title:${item.title || ''}`;
  }

  function normalize(raw) {
    return {
      id: raw.id != null && raw.id !== '' ? String(raw.id) : '',
      handle: raw.handle || '',
      title: raw.title || '',
      url: raw.url || '',
      image: raw.image || '',
      price: raw.price != null && raw.price !== '' ? Number(raw.price) : 0,
      /* Avise-me (spec 0133): ausente = disponível (compat seed antigo). */
      available: raw.available !== false,
      variantId: raw.variantId != null ? String(raw.variantId) : '',
    };
  }

  function getItems() {
    if (!useRemote()) return [];
    if (memoryItems) return memoryItems.slice();
    if (boot.serverItems.length) return boot.serverItems.slice();
    return [];
  }

  function setMemory(items) {
    memoryItems = items.map(normalize);
    return memoryItems;
  }

  function list() {
    return getItems();
  }

  function has(raw) {
    const key = itemKey(normalize(raw));
    return getItems().some((item) => itemKey(item) === key);
  }

  async function remoteFetch(path, options) {
    const base = boot.proxyBase || PROXY_DEFAULT;
    let url = path ? `${base}${path}` : base;
    const opts = options || {};
    const productId =
      opts.productId != null && String(opts.productId).trim() !== ''
        ? String(opts.productId).trim()
        : null;

    /* App Proxy: productId na query + form (JSON no body às vezes chega vazio). */
    if (productId) {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      url = `${url}${sep}productId=${encodeURIComponent(productId)}`;
    }

    const headers = {
      Accept: 'application/json',
      ...(opts.headers || {}),
    };

    let body = opts.body;
    if (productId && opts.method && opts.method !== 'GET') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
      body = new URLSearchParams({ productId }).toString();
    }

    const { productId: _drop, body: _b, headers: _h, ...rest } = opts;
    const res = await fetch(url, {
      credentials: 'same-origin',
      headers,
      ...rest,
      body,
    });
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    return { res, data };
  }

  function enqueueRemote(fn) {
    remoteQueue = remoteQueue.then(fn, fn);
    return remoteQueue;
  }

  function applyOptimisticAdd(raw) {
    const item = normalize(raw);
    if (!item.title && !item.handle && !item.id) return list();
    const items = getItems();
    const key = itemKey(item);
    if (items.some((existing) => itemKey(existing) === key)) return items;
    items.push(item);
    setMemory(items);
    emit(items);
    return items;
  }

  function applyOptimisticRemove(raw) {
    const key = itemKey(normalize(raw));
    const next = getItems().filter((item) => itemKey(item) !== key);
    setMemory(next);
    emit(next);
    return next;
  }

  function add(raw) {
    if (!useRemote()) {
      requireLogin();
      return list();
    }

    const item = normalize(raw);
    if (!item.id) {
      console.warn('[GmWishlist] toggle sem product id numérico');
      return list();
    }
    if (has(item)) return list();
    applyOptimisticAdd(item);

    enqueueRemote(async () => {
      try {
        const { res, data } = await remoteFetch('/toggle', {
          method: 'POST',
          productId: item.id,
        });
        if (!res.ok || !data || !data.ok) {
          applyOptimisticRemove(item);
          console.warn('[GmWishlist] toggle add falhou', res.status, data);
          return;
        }
        if (data.pressed === false) {
          const again = await remoteFetch('/toggle', {
            method: 'POST',
            productId: item.id,
          });
          if (again.data && Array.isArray(again.data.items)) {
            setMemory(again.data.items);
            emit(again.data.items);
            return;
          }
        }
        if (Array.isArray(data.items)) {
          setMemory(data.items);
          emit(data.items);
        }
      } catch (e) {
        applyOptimisticRemove(item);
      }
    });

    return list();
  }

  function remove(raw) {
    if (!useRemote()) {
      requireLogin();
      return list();
    }

    const item = normalize(raw);
    if (!item.id) {
      console.warn('[GmWishlist] toggle sem product id numérico');
      return list();
    }
    if (!has(item)) return list();
    applyOptimisticRemove(item);

    enqueueRemote(async () => {
      try {
        const { res, data } = await remoteFetch('/toggle', {
          method: 'POST',
          productId: item.id,
        });
        if (!res.ok || !data || !data.ok) {
          applyOptimisticAdd(item);
          console.warn('[GmWishlist] toggle remove falhou', res.status, data);
          return;
        }
        if (data.pressed === true) {
          const again = await remoteFetch('/toggle', {
            method: 'POST',
            productId: item.id,
          });
          if (again.data && Array.isArray(again.data.items)) {
            setMemory(again.data.items);
            emit(again.data.items);
            return;
          }
        }
        if (Array.isArray(data.items)) {
          setMemory(data.items);
          emit(data.items);
        }
      } catch (e) {
        applyOptimisticAdd(item);
      }
    });

    return list();
  }

  function toggle(raw) {
    if (!useRemote()) {
      requireLogin();
      return false;
    }
    if (has(raw)) {
      remove(raw);
      return false;
    }
    add(raw);
    return true;
  }

  function fromEl(el) {
    if (!el) return null;
    return {
      id: el.dataset.productId || '',
      handle: el.dataset.productHandle || '',
      title: el.dataset.productTitle || '',
      url: el.dataset.productUrl || '',
      image: el.dataset.productImage || '',
      price: el.dataset.productPrice || 0,
    };
  }

  function labelFor(el, pressed) {
    const title = el.dataset.productTitle || '';
    const addTpl = el.dataset.labelAdd || 'Adicionar à lista de desejos';
    const removeTpl = el.dataset.labelRemove || 'Remover da lista de desejos';
    const prefix = pressed ? removeTpl : addTpl;
    return title ? `${prefix}: ${title}` : prefix;
  }

  function syncEl(el) {
    const data = fromEl(el);
    if (!data) return;
    const pressed = useRemote() && has(data);
    el.setAttribute('aria-pressed', String(pressed));
    el.setAttribute('aria-label', labelFor(el, pressed));
  }

  function syncAll(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-gm-wishlist], [data-gm-card-favorite]').forEach(syncEl);
  }

  /** Botão de adicionar do app na PDP (estacionado fora da tela). */
  function appProductButton() {
    return document.querySelector(
      '#wishlisthero-product-page-button-container button, #wishlist-hero-product-page-button button',
    );
  }

  /** Launcher flutuante do app (abre a lista). */
  function appLauncher() {
    return document.querySelector('#wishlisthero-floating-launcher-container a');
  }

  function openWishList() {
    const launcher = appLauncher();
    if (!launcher) {
      window.__gmWishOpenPending = true;
      return false;
    }
    window.__gmWishOpenPending = false;
    clickApp(launcher);
    return true;
  }

  function appButtonFor(el) {
    if (el.hasAttribute('data-gm-card-favorite')) {
      const card =
        el.closest('li, .gm-product-card, product-card, .product-card') ||
        el.parentElement;
      if (card) {
        const local = card.querySelector(
          '.wishlist-hero-custom-button button, .wishlist-hero-custom-button a, [data-wlh-id] button, [data-wlh-id] a',
        );
        if (local && local !== el && !el.contains(local)) return local;
      }
    }
    if (el.hasAttribute('data-gm-wishlist')) return appProductButton();
    return null;
  }

  function clickApp(node) {
    if (!node) return false;
    if (node.tagName === 'A') {
      node.addEventListener(
        'click',
        function stopHash(ev) {
          if (ev.target === node || node.contains(ev.target)) ev.preventDefault();
        },
        { capture: true, once: true },
      );
    }
    node.click();
    return true;
  }

  function appLooksSaved(btn) {
    const label = (
      (btn.getAttribute('aria-label') || '') +
      ' ' +
      (btn.getAttribute('title') || '')
    ).toLowerCase();
    return (
      label.indexOf('remover') !== -1 ||
      label.indexOf('remove') !== -1 ||
      label.indexOf('na lista') !== -1
    );
  }

  function mirrorAppHearts() {
    const btn = appProductButton();
    if (!btn) return;
    const pressed = appLooksSaved(btn);
    document.querySelectorAll('[data-gm-wishlist]').forEach((heart) => {
      heart.setAttribute('aria-pressed', String(pressed));
      heart.setAttribute('aria-label', labelFor(heart, pressed));
    });
  }

  function brlFromApp(text) {
    const match = String(text || '').match(/R\$\s*([\d.,]+)/);
    if (!match) return null;
    const raw = match[1];
    let num;
    const comma = raw.lastIndexOf(',');
    const dot = raw.lastIndexOf('.');
    if (comma !== -1 && dot !== -1) {
      num =
        comma > dot
          ? Number(raw.replace(/\./g, '').replace(',', '.'))
          : Number(raw.replace(/,/g, ''));
    } else if (comma !== -1) {
      num = Number(raw.replace(/\./g, '').replace(',', '.'));
    } else {
      num = Number(raw.replace(/,/g, ''));
    }
    if (!Number.isFinite(num)) return null;
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(num);
    } catch (e) {
      return null;
    }
  }

  /** Handles mais longos primeiro (Ana Giga antes de Ana). Jornada não está no modelos.json. */
  const WISH_MODELOS = [
    ['protetor-impermeabilizante-nobuck-camurca', 'Protetor Impermeabilizante Nobuck | Camurça'],
    ['alca-avulsa-pochete-rafa', 'Alça Avulsa Pochete Rafa'],
    ['bolsa-natasha-baguete', 'Bolsa Natasha Baguete'],
    ['guarda-cartao-gm-116', 'Guarda Cartão GM 116'],
    ['kit-presente-guarda', 'Kit Presente Guarda'],
    ['bolsa-dani-pequena', 'Bolsa Dani Pequena'],
    ['necessaire-vanessa', 'Necessaire Vanessa'],
    ['bolsa-ana-pequena', 'Bolsa Ana Pequena'],
    ['bolsa-pochete-ste', 'Bolsa Pochete Ste'],
    ['guarda-cartao-lay', 'Guarda Cartão Lay'],
    ['bolsa-ana-giga', 'Bolsa Ana Giga'],
    ['bolsa-jornada', 'Bolsa Jornada'],
    ['cartao-guarda', 'Cartão Guarda'],
    ['guarda-oculos', 'Guarda Óculos'],
    ['bolsa-nicole', 'Bolsa Nicole'],
    ['guarda-chave', 'Guarda Chave'],
    ['pochete-rafa', 'Pochete Rafa'],
    ['tote-vanessa', 'Tote Vanessa'],
    ['bolsa-fefa', 'Bolsa Fefa'],
    ['bolsa-lena', 'Bolsa Lena'],
    ['bolsa-nena', 'Bolsa Nena'],
    ['cinto-rita', 'Cinto Rita'],
    ['bolsa-ana', 'Bolsa Ana'],
    ['tote-eva', 'Tote Eva'],
    ['tote-van', 'Tote Van'],
  ];

  function foldWish(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function stripModelPrefix(title, model) {
    const words = String(title || '').split(/\s+/).filter(Boolean);
    const modelWords = foldWish(model).split(' ').filter(Boolean);
    const folded = words.map(foldWish);
    let index = 0;
    for (const word of modelWords) {
      if (folded[index] !== word) break;
      index += 1;
    }
    if (!index) return '';
    return words.slice(index).join(' ');
  }

  function splitWishTitle(full, href) {
    const raw = String(full || '').replace(/\s+/g, ' ').trim();
    let title = raw;
    let size = '';
    const cut = raw.lastIndexOf(' - ');
    if (cut > 0) {
      const maybe = raw.slice(cut + 3).trim();
      const folded = foldWish(maybe);
      title = raw.slice(0, cut).trim();
      if (
        folded &&
        folded !== 'unico' &&
        folded !== 'default' &&
        folded !== 'default title' &&
        folded !== 'title'
      ) {
        size = maybe;
      }
    }
    const handle = String(href || '').split('/products/')[1] || '';
    const productHandle = handle.split('?')[0].split('#')[0];
    let model = '';
    for (const pair of WISH_MODELOS) {
      const modeloHandle = pair[0];
      if (
        productHandle === modeloHandle ||
        productHandle.startsWith(`${modeloHandle}-`)
      ) {
        model = pair[1];
        break;
      }
    }
    let edition = '';
    if (model) {
      edition = stripModelPrefix(title, model);
      if (!edition && foldWish(title) !== foldWish(model)) edition = title;
    } else {
      model = title;
    }
    if (size) edition = edition ? `${edition} · ${size}` : size;
    return { model: model || title, edition };
  }

  function paintWishName(el) {
    if (!el) return;
    const painted = el.querySelector('.gm-wl-name');
    const current = el.textContent.replace(/\s+/g, ' ').trim();
    const raw = painted ? el.dataset.gmRaw || current : current;
    if (!painted) el.dataset.gmRaw = raw;
    const href = el.getAttribute('href') || '';
    const parts = splitWishTitle(raw, href);
    const key = `${parts.model}|${parts.edition}`;
    if (el.dataset.gmPainted === key && painted) return;
    el.dataset.gmRaw = raw;
    el.dataset.gmPainted = key;
    el.replaceChildren();
    const name = document.createElement('span');
    name.className = 'gm-wl-name';
    name.textContent = parts.model;
    const edition = document.createElement('span');
    edition.className = 'gm-wl-edition';
    edition.textContent = parts.edition;
    el.append(name, edition);
  }

  function paintWishNames() {
    document
      .querySelectorAll(
        '.wishlist-hero-list-card-header-product-name, #wishlist-hero-add-notification .MuiCardContent-root > a',
      )
      .forEach(paintWishName);
  }

  let wishOrderKeys = null;

  function wishCardKey(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';
    const handle = (link.getAttribute('href') || '').split('/products/')[1] || '';
    return handle.split('?')[0].split('#')[0];
  }

  function pinWishOrder() {
    const grid = document.querySelector('.MuiDialog-paper .MuiGrid-container');
    if (!grid) return;
    const cards = [...grid.querySelectorAll(':scope > .wishlist-hero-list-card')];
    const keys = cards.map(wishCardKey).filter(Boolean);
    if (!keys.length) return;
    const reversed =
      wishOrderKeys &&
      keys.length === wishOrderKeys.length &&
      keys.every((key, index) => key === wishOrderKeys[wishOrderKeys.length - 1 - index]);
    if (!wishOrderKeys) {
      wishOrderKeys = keys.slice();
    } else if (!reversed) {
      keys.forEach((key) => {
        if (!wishOrderKeys.includes(key)) wishOrderKeys.push(key);
      });
      wishOrderKeys = wishOrderKeys.filter((key) => keys.includes(key));
    }
    cards.forEach((card) => {
      const index = wishOrderKeys.indexOf(wishCardKey(card));
      if (index >= 0) card.style.order = String(index);
    });
  }

  function syncWishScrollLock() {
    const open = Boolean(document.querySelector('.MuiDialog-paper .wishlist-hero-header'));
    const root = document.documentElement;
    if (open) {
      if (!root.classList.contains('gm-wl-scroll-lock')) {
        const y = window.scrollY || window.pageYOffset || 0;
        root.style.setProperty('--gm-scroll-y', `${y}px`);
        root.classList.add('gm-wl-scroll-lock');
      }
      return;
    }
    if (!root.classList.contains('gm-wl-scroll-lock')) return;
    const y = parseFloat(root.style.getPropertyValue('--gm-scroll-y')) || 0;
    root.classList.remove('gm-wl-scroll-lock');
    window.scrollTo(0, y);
  }

  function dressWishFooterIcon(button, kind) {
    if (button.querySelector(':scope > .gm-wl-footer-icon')) return;
    const paths = {
      cart: '<path d="M3 3h2l2.2 12h11l2-8H6M14 4v6m-3-3h6"/><circle cx="8" cy="20" r="1"/><circle cx="18" cy="20" r="1"/>',
      email: '<rect x="3" y="5" width="18" height="14" rx="1"/><path d="m3 6 9 7 9-7"/>',
      share: '<path d="M12 15V3m-4 4 4-4 4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"/>',
    };
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.classList.add('gm-wl-footer-icon');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    icon.innerHTML = paths[kind];
    button.prepend(icon);
  }

  function dressWishFooter() {
    const paper = document.querySelector('.MuiDialog-paper:has(.wishlist-hero-header)');
    if (!paper) return;
    const drawer = !document.documentElement.classList.contains('gm-wl-modal');
    if (drawer) {
      paper.querySelectorAll('.wishlist-hero-footer button').forEach((button) => {
        const label = foldWish(button.textContent);
        const kind = label.includes('compartilhar')
          ? 'share'
          : /e ?mail/.test(label) ? 'email' : '';
        if (!kind) return;
        button.classList.add('gm-wl-footer-utility');
        dressWishFooterIcon(button, kind);
      });
      const utilities = [...paper.querySelectorAll('.wishlist-hero-footer .gm-wl-footer-utility')];
      if (utilities.length > 1) {
        let row = utilities[0].parentElement;
        while (row && row !== paper && !utilities.every((button) => row.contains(button))) {
          row = row.parentElement;
        }
        if (row && row !== paper) row.classList.add('gm-wl-utility-row');
      }
    }
    const marked = [];
    paper.querySelectorAll('button').forEach((btn) => {
      const label = (btn.textContent || '').trim().toLowerCase();
      const add = label.includes('adicionar') && label.includes('todos');
      const remove = label.includes('remover') && label.includes('todos');
      btn.classList.toggle('gm-wl-footer-add', add);
      btn.classList.toggle('gm-wl-footer-remove', remove);
      if (add && drawer) dressWishFooterIcon(btn, 'cart');
      if (add || remove) marked.push(btn);
    });
    if (!marked.length) return;
    let parent = marked[0].parentElement;
    while (parent && parent !== paper && !marked.every((btn) => parent.contains(btn))) {
      parent = parent.parentElement;
    }
    if (!parent || parent === paper) return;
    if (parent.querySelector('.wishlist-hero-list-card')) {
      marked.forEach((btn) => {
        const wrap = btn.parentElement;
        if (wrap && !wrap.querySelector('.wishlist-hero-list-card')) wrap.classList.add('gm-wl-footer');
      });
      return;
    }
    parent.classList.add('gm-wl-footer');
  }

  function pinWishDock() {
    if (document.documentElement.classList.contains('gm-wl-modal')) return;
    const paper = document.querySelector('.MuiDialog-paper:has(.wishlist-hero-header)');
    if (!paper) return;
    const footer = paper.querySelector('.wishlist-hero-list-footer');
    if (!footer) return;
    const actions = paper.querySelector('.wishlist-hero-footer, .MuiDialogActions-root');
    let dock = paper.querySelector(':scope > .gm-wl-dock');
    if (!dock) {
      dock = document.createElement('div');
      dock.className = 'gm-wl-dock';
      if (actions) paper.insertBefore(dock, actions);
      else paper.appendChild(dock);
    }
    if (footer.parentElement !== dock) dock.appendChild(footer);
  }

  function fitWishTitle() {
    if (document.documentElement.classList.contains('gm-wl-modal')) return;
    const title = document.querySelector('.MuiDialog-paper .wishlist-hero-header-title');
    if (!title) return;
    title.style.removeProperty('font-size');
    let size = parseFloat(getComputedStyle(title).fontSize);
    if (!size) return;
    for (let i = 0; i < 48 && title.scrollWidth > title.clientWidth + 1 && size > 13; i += 1) {
      size -= 0.5;
      title.style.setProperty('font-size', `${size}px`, 'important');
    }
  }

  function dressSharedWishPage() {
    const root = document.getElementById('wishlist-hero-shared-list-view');
    if (!root) return;
    const drawer = !document.documentElement.classList.contains('gm-wl-modal');
    root.querySelectorAll('.wishlist-hero-page-actions-bar button').forEach((button) => {
      const label = foldWish(button.textContent);
      const kind = label.includes('compartilhar')
        ? 'share'
        : /e ?mail/.test(label) ? 'email' : '';
      if (!kind) return;
      button.classList.add('gm-wl-footer-utility', 'cursor-pointer');
      if (drawer) dressWishFooterIcon(button, kind);
    });
    root.querySelectorAll('button').forEach((button) => {
      const label = foldWish(button.textContent);
      const add = label.includes('adicionar') && label.includes('todos');
      if (!add) return;
      button.classList.add('gm-wl-footer-add', 'cursor-pointer');
      if (drawer) dressWishFooterIcon(button, 'cart');
    });
  }

  function dressHeroUi() {
    syncWishScrollLock();
    pinWishOrder();
    paintWishNames();
    dressWishFooter();
    dressSharedWishPage();
    pinWishDock();
    fitWishTitle();
    const thumb = document.querySelector(
      '#wishlist-hero-add-notification .MuiCardContent-root > div',
    );
    if (thumb) {
      thumb.style.setProperty('flex', '0 0 56px', 'important');
      thumb.style.setProperty('width', '56px', 'important');
      thumb.style.setProperty('height', '70px', 'important');
      thumb.style.setProperty('background-size', 'cover', 'important');
      thumb.style.setProperty('background-position', 'center', 'important');
    }
    document
      .querySelectorAll(
        '#wishlist-hero-add-notification .MuiCardActions-root a, #wishlist-hero-add-notification button, .wishlist-hero-list-card button, .wishlist-hero-list-card a, .MuiDialog-paper .MuiButton-text, .wishlist-hero-header-close',
      )
      .forEach((el) => {
        el.classList.add('cursor-pointer');
      });
    document.querySelectorAll('.wishlist-hero-list-card .MuiIconButton-root').forEach((btn) => {
      btn.setAttribute('data-gm-tip', 'Remover da lista de desejos');
      btn.setAttribute('data-gm-tip-place', 'above');
      btn.removeAttribute('title');
    });
    document.querySelectorAll('.wishlist-hero-list-card .MuiButton-contained').forEach((btn) => {
      const label = (btn.textContent || '').trim().toLowerCase();
      if (label === 'comprar') {
        btn.setAttribute('data-gm-tip', 'Adicionar ao carrinho');
        btn.setAttribute('data-gm-tip-place', 'above-center');
      }
      btn.removeAttribute('title');
    });
    document
      .querySelectorAll(
        '.wishlist-hero-price-update .money, .wishlist-hero-list-card-product-price .money',
      )
      .forEach((el) => {
        const next = brlFromApp(el.textContent);
        if (next && el.textContent.trim() !== next) el.textContent = next;
      });
  }

  function watchHeroUi() {
    if (document.documentElement.dataset.gmHeroUi === '1') return;
    document.documentElement.dataset.gmHeroUi = '1';
    let queued = false;
    const run = () => {
      queued = false;
      dressHeroUi();
    };
    const mo = new MutationObserver(() => {
      pinWishOrder();
      syncWishScrollLock();
      if (window.__gmWishOpenPending) openWishList();
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(run);
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    dressHeroUi();
  }

  function watchProductButton() {
    const host = document.getElementById('wishlist-hero-product-page-button');
    if (!host || host.dataset.gmWatched === '1') return;
    host.dataset.gmWatched = '1';
    const mo = new MutationObserver(() => mirrorAppHearts());
    mo.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label', 'title', 'class'],
    });
    mirrorAppHearts();
  }

  function onOpenLauncher(e) {
    const open = e.target.closest('[data-gm-wishlist-open]');
    if (!open) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.__gmWishOpened) return;
    e.__gmWishOpened = true;
    openWishList();
  }

  function onClick(e) {
    const el = e.target.closest('[data-gm-wishlist], [data-gm-card-favorite]');
    if (!el) return;

    e.preventDefault();
    e.stopPropagation();

    const appBtn = appButtonFor(el);
    if (appBtn) {
      clickApp(appBtn);
      window.setTimeout(mirrorAppHearts, 50);
      window.setTimeout(mirrorAppHearts, 400);
    }

    if (!useRemote()) {
      if (appBtn) return;
      requireLogin();
      return;
    }

    const data = fromEl(el);
    if (!data) return;
    const pressed = toggle(data);
    el.setAttribute('aria-pressed', String(pressed));
    el.setAttribute('aria-label', labelFor(el, pressed));
    if (typeof el.blur === 'function') el.blur();
  }

  function formatMoney(cents) {
    const value = Number(cents) || 0;
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value / 100);
    } catch (e) {
      return `R$ ${(value / 100).toFixed(2)}`;
    }
  }

  /** Esgotado: variante única → Avise-me 1 clique; multi → escolher na PDP. */
  function notifyControl(item) {
    if (!item.variantId) {
      const a = document.createElement('a');
      a.className = 'gm-wishlist-page__notify';
      a.href = item.url ? `${item.url}#gm-notify` : '#';
      a.textContent = 'Avise-me: escolher tamanho';
      return a;
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gm-wishlist-page__notify cursor-pointer';
    let active = false;
    const setActive = (on) => {
      active = on;
      btn.textContent = on ? 'Aviso ativado · cancelar' : 'Avise-me quando voltar';
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    };
    setActive(false);
    /* GmNotify carrega depois (defer); re-render pós-sync já o encontra. */
    if (window.GmNotify) {
      window.GmNotify.status(item.variantId).then(setActive);
    }
    btn.addEventListener('click', async () => {
      if (!window.GmNotify) return;
      btn.disabled = true;
      const data = active
        ? await window.GmNotify.cancel({ variantId: item.variantId })
        : await window.GmNotify.subscribe({
            variantId: item.variantId,
            productId: item.id,
            title: item.title,
          });
      btn.disabled = false;
      if (data.ok) {
        setActive(!active);
        return;
      }
      btn.textContent =
        data.error === 'in_stock' ? 'Já disponível — ver produto' : 'Tente de novo';
      if (data.error === 'in_stock' && item.url) window.location.href = item.url;
    });
    return btn;
  }

  function renderPage(root) {
    if (!root) return;
    const listEl = root.querySelector('[data-gm-wishlist-list]');
    const emptyEl = root.querySelector('[data-gm-wishlist-empty]');
    const loginEl = root.querySelector('[data-gm-wishlist-login]');
    if (!listEl) return;

    if (!useRemote()) {
      if (loginEl) loginEl.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
      listEl.hidden = true;
      listEl.innerHTML = '';
      return;
    }

    if (loginEl) loginEl.hidden = true;

    if (!emptyEl) return;

    const items = list();
    listEl.innerHTML = '';

    if (items.length === 0) {
      emptyEl.hidden = false;
      listEl.hidden = true;
      return;
    }

    emptyEl.hidden = true;
    listEl.hidden = false;

    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'gm-wishlist-page__item';

      const link = document.createElement(item.url ? 'a' : 'div');
      if (item.url) {
        link.href = item.url;
        link.className = 'gm-wishlist-page__link';
      } else {
        link.className = 'gm-wishlist-page__link gm-wishlist-page__link--static';
      }

      const media = document.createElement('span');
      media.className = 'gm-wishlist-page__media';
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title || '';
        img.width = 400;
        img.height = 500;
        img.loading = 'lazy';
        media.appendChild(img);
      }

      const meta = document.createElement('span');
      meta.className = 'gm-wishlist-page__meta';
      const title = document.createElement('span');
      title.className = 'gm-wishlist-page__title';
      title.textContent = item.title || item.handle || 'Produto';
      meta.appendChild(title);
      if (item.price > 0) {
        const price = document.createElement('span');
        price.className = 'gm-wishlist-page__price';
        price.textContent = formatMoney(item.price);
        meta.appendChild(price);
      }

      if (!item.available) {
        const badge = document.createElement('span');
        badge.className = 'gm-wishlist-page__badge';
        badge.textContent = 'Esgotado';
        meta.appendChild(badge);
      }

      link.appendChild(media);
      link.appendChild(meta);
      li.appendChild(link);

      if (!item.available) li.appendChild(notifyControl(item));

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'gm-wishlist-page__remove cursor-pointer';
      removeBtn.setAttribute('aria-label', `Remover ${item.title || 'produto'} da lista`);
      removeBtn.textContent = 'Remover';
      removeBtn.addEventListener('click', () => {
        remove(item);
      });
      li.appendChild(removeBtn);

      listEl.appendChild(li);
    });
  }

  async function initRemote() {
    if (!useRemote()) return;

    if (boot.serverItems.length) {
      setMemory(boot.serverItems);
    }

    try {
      const { res, data } = await remoteFetch('', { method: 'GET' });
      if (res.ok && data && data.ok && Array.isArray(data.items)) {
        setMemory(data.items);
        emit(data.items);
      } else if (res.status === 401 || res.status === 503) {
        /* Sem fallback local: mantém seed Liquid se houver; senão vazio. */
        if (!boot.serverItems.length) {
          setMemory([]);
          emit([]);
        }
      }
    } catch (e) {
      if (!boot.serverItems.length) {
        setMemory([]);
        emit([]);
      }
    }
  }

  window.GmWishlist = {
    list,
    has,
    add,
    remove,
    toggle,
    syncAll,
    renderPage,
    requireLogin,
    openList: openWishList,
    CHANGE_EVENT,
    isLoggedIn,
  };

  function wishTipEl() {
    let tip = document.getElementById('gm-wishlist-tip');
    if (tip) return tip;
    tip = document.createElement('div');
    tip.id = 'gm-wishlist-tip';
    tip.className = 'gm-wishlist-tip';
    tip.hidden = true;
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
    return tip;
  }

  function tipTarget(node) {
    if (!node || !node.closest) return null;
    return node.closest('[data-gm-wishlist], [data-gm-tip]');
  }

  function showWishTip(el) {
    if (!el || window.matchMedia('(hover: none)').matches) return;
    const tip = wishTipEl();
    const rect = el.getBoundingClientRect();
    tip.style.left = `${rect.left + rect.width / 2}px`;
    if (el.hasAttribute('data-gm-wishlist')) {
      const pressed = el.getAttribute('aria-pressed') === 'true';
      tip.textContent = pressed
        ? el.dataset.labelRemove || 'Remover da lista de desejos'
        : el.dataset.labelAdd || 'Adicionar à lista de desejos';
      tip.style.top = `${rect.top}px`;
      tip.style.transform = 'translate(-50%, calc(-100% - 8px))';
    } else {
      tip.textContent = el.getAttribute('data-gm-tip') || '';
      if (el.getAttribute('data-gm-tip-place') === 'above') {
        tip.style.top = `${rect.top}px`;
        tip.style.left = `${rect.right}px`;
        tip.style.transform = 'translate(-100%, calc(-100% - 8px))';
      } else if (el.getAttribute('data-gm-tip-place') === 'above-center') {
        tip.style.top = `${rect.top}px`;
        tip.style.transform = 'translate(-50%, calc(-100% - 8px))';
      } else {
        tip.style.top = `${rect.bottom}px`;
        tip.style.transform = 'translate(-50%, 8px)';
      }
    }
    if (!tip.textContent) return;
    tip.hidden = false;
  }

  function hideWishTip() {
    const tip = document.getElementById('gm-wishlist-tip');
    if (tip) tip.hidden = true;
  }

  document.addEventListener('pointerover', (e) => {
    const el = tipTarget(e.target);
    if (!el) return;
    showWishTip(el);
  });
  document.addEventListener('pointerout', (e) => {
    const el = tipTarget(e.target);
    if (!el || el.contains(e.relatedTarget)) return;
    hideWishTip();
  });
  document.addEventListener('focusin', (e) => {
    const el = tipTarget(e.target);
    if (el) showWishTip(el);
  });
  document.addEventListener('focusout', (e) => {
    const el = tipTarget(e.target);
    if (el) hideWishTip();
  });
  window.addEventListener('scroll', () => hideWishTip(), true);

  document.addEventListener('click', onOpenLauncher);
  document.addEventListener('click', onClick);
  document.addEventListener(CHANGE_EVENT, () => {
    syncAll();
    document.querySelectorAll('[data-gm-wishlist-page]').forEach(renderPage);
  });

  function bootAndRender() {
    readBoot();
    clearLegacyStorage();
    if (useRemote() && boot.serverItems.length) {
      setMemory(boot.serverItems);
    }
    syncAll();
    document.querySelectorAll('[data-gm-wishlist-page]').forEach(renderPage);
    watchProductButton();
    watchHeroUi();
    if (window.__gmWishOpenPending) openWishList();
    initRemote();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAndRender);
  } else {
    bootAndRender();
  }
})();
